/**
 * Onboarding client
 * -----------------
 * Typed interface for reading and writing merchant onboarding state, matching
 * the state model in docs `workflow.md`.
 *
 * Storage is split on purpose:
 *
 *   SERVER (`GET/PATCH /onboarding`, backed by the existing `feedback_details`
 *   row) holds the one fact that has to outlive a browser: whether the merchant
 *   is done with the form. Before this existed, "Save & exit" was forgotten the
 *   moment they signed in from another device or cleared site data, and the
 *   wizard came back as if they had never been through it.
 *
 *   LOCALSTORAGE holds the in-progress draft -- which step they are on, the
 *   half-typed business form, the use cases not yet confirmed. There is no
 *   column for a partial step, and resume position is a convenience rather than
 *   something that has to survive a new device.
 *
 * When the two disagree about dismissal the server wins, because it is the one
 * that knows about the other browser.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/*
 * Imported at call time, not at module scope, to break an import cycle:
 *
 *   utils/api -> store/index -> store/api/onboardingApi -> this module
 *
 * A static `import { fetchWithAuthJSON } from '../../utils/api'` closes that
 * loop. Webpack then evaluates this file while store/index is still mid-flight,
 * and the first thing to touch `onboardingApi` throws
 * "Cannot access 'onboardingApi' before initialization" -- a temporal dead zone
 * error that takes the whole app down at boot, not just onboarding.
 *
 * By the time any of the functions below actually run, the store is long since
 * built, so resolving the module lazily is free of that ordering problem.
 */
const authFetch = async (url, options) => {
  const { fetchWithAuthJSON } = await import('../../utils/api');
  return fetchWithAuthJSON(url, options);
};

/**
 * @typedef {'started'|'business_done'|'usecases_done'|'activation_done'|'payment_done'|'completed'} OnboardingStatus
 * @typedef {'invoice'|'api'} OnboardingPath
 * @typedef {'none'|'pending'|'succeeded'} FirstTestPayment
 * @typedef {'not_started'|'kyc_pending'|'wallet_added'|'live'} GoLiveStatus
 */

/**
 * @typedef {Object} BusinessProfile
 * @property {string} name
 * @property {string} website
 * @property {string} type
 */

/**
 * @typedef {Object} OnboardingState
 * @property {OnboardingStatus} onboarding_status
 * @property {OnboardingPath|null} onboarding_path
 * @property {FirstTestPayment} first_test_payment
 * @property {GoLiveStatus} go_live_status
 * @property {BusinessProfile} business
 * @property {string[]} use_cases          - selected use-case ids
 * @property {string|null} primary_use_case - id of the primary use case
 * @property {string} volume               - optional segmentation
 * @property {string} source               - optional "how did you hear" value
 * @property {boolean} onboarding_dismissed - merchant left setup on purpose; the
 *   wizard must not be put in front of them again. Server-owned.
 * @property {boolean} onboarding_applicable - false for super admins, who have no
 *   merchant account to set up. Server-owned.
 */

/** @returns {OnboardingState} */
const defaultState = () => ({
  onboarding_status: 'started',
  onboarding_path: null,
  first_test_payment: 'none',
  go_live_status: 'not_started',
  business: { name: '', website: '', type: '' },
  use_cases: [],
  primary_use_case: null,
  volume: '',
  source: '',
  onboarding_dismissed: false,
  onboarding_applicable: true,
});

// Scope the mock per account so switching users does not bleed state.
const storageKey = (userId) => `maxelpay_onboarding_${userId || 'anon'}`;

/** Local draft state only. Never the authority on dismissal. */
function readLocal(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return defaultState();
    return { ...defaultState(), ...JSON.parse(raw) };
  } catch {
    return defaultState();
  }
}

/**
 * Server-owned onboarding facts, or null when they cannot be read.
 *
 * Null rather than a throw: losing the network, or hitting a backend that has
 * not been restarted with the `/onboarding` route yet, must not block the
 * wizard. The caller falls back to local state, which is how this worked before
 * the endpoint existed.
 */
async function readServer() {
  try {
    const res = await authFetch(`${API_URL}/onboarding`);
    return res?.data || null;
  } catch {
    return null;
  }
}

/**
 * Fetch the current onboarding state for a merchant.
 *
 * Local draft state, with the server's view of dismissal layered on top. The
 * server wins on `onboarding_dismissed` and `onboarding_status` because those
 * are the fields another device may have changed.
 *
 * @param {string|number} userId
 * @returns {Promise<OnboardingState>}
 */
export async function getOnboarding(userId) {
  const local = readLocal(userId);
  const server = await readServer();
  if (!server) return local;

  return {
    ...local,
    // The server only ever reports 'completed' or null -- it does not store a
    // partial step -- so a null must not wipe the local resume position.
    onboarding_status: server.onboarding_status || local.onboarding_status,
    onboarding_dismissed: Boolean(server.onboarding_dismissed) || local.onboarding_dismissed,
    onboarding_applicable: server.applicable !== false,
  };
}

/**
 * Project the wizard's local state onto the camelCase field names the
 * `feedback_details` row uses. Empty values are dropped by the server, so a
 * merchant who exits on step 1 simply writes fewer columns.
 */
function feedbackFieldsFrom(state) {
  return {
    businessName: state.business?.name || '',
    websiteUrl: state.business?.website || '',
    businessType: state.business?.type || '',
    paymentUsecase: state.use_cases?.length ? state.use_cases : undefined,
    expectedMonthlyVolume: state.volume || '',
  };
}

/**
 * Merge a partial update into the merchant's onboarding state.
 * @param {string|number} userId
 * @param {Partial<OnboardingState>} patch
 * @returns {Promise<OnboardingState>}
 */
export async function updateOnboarding(userId, patch) {
  const current = readLocal(userId);
  const next = {
    ...current,
    ...patch,
    business: { ...current.business, ...(patch.business || {}) },
  };
  localStorage.setItem(storageKey(userId), JSON.stringify(next));

  // Mirror the dismissal to the server. Written after the local save so a failed
  // request still leaves the merchant's own browser consistent -- the worst case
  // is that the exit is remembered here but not on their next device, which is
  // where this started.
  //
  // The answers collected so far go with it, so leaving halfway does not throw
  // away what was typed: it lands in the same feedback_details columns a full
  // submit would have used.
  if (patch.onboarding_dismissed) {
    try {
      await authFetch(`${API_URL}/onboarding`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dismissed: true, fields: feedbackFieldsFrom(next) }),
      });
    } catch (err) {
      console.warn('[Onboarding] could not save state to the server:', err.message);
    }
  }

  return next;
}

/**
 * Simulate a Test-mode payment landing against a created invoice/session.
 * Returns 'succeeded' so the merchant sees the gateway work end to end.
 * @param {string|number} userId
 * @returns {Promise<{ first_test_payment: FirstTestPayment }>}
 */
export async function simulateTestPayment(userId) {
  // TODO(backend): POST `/onboarding/test-payment/simulate` against the real
  // test session. For now we flip local state to succeeded after a short delay.
  await updateOnboarding(userId, { first_test_payment: 'succeeded' });
  return { first_test_payment: 'succeeded' };
}

export { defaultState };
